import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Heading,
  Icon,
  LinkButton,
  Stack,
  Text,
} from '@eluve/components';
import {
  GlobalBundleBlocker,
  getActiveBundleBlockersQuery,
  groupGlobalBlockersByFk,
} from '@eluve/frontend-feature-prompt-templates';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';

import { ToggleOutputTemplateActiveAction } from './ToggleOutputTemplateActiveAction';

export interface OutputTemplatesListPageProps {}

const getOutputTemplatesQuery = graphql(`
  query getOutputTemplates {
    llmOutputTemplates(where: { tenantId: { _isNull: true } }) {
      __typename
      id
      name
      description
      llmOutputType
      isActive
      createdAt
      variantsAggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

type OutputTemplateRow = {
  id: string;
  name: string;
  description: string;
  outputType: string;
  isActive: boolean;
  createdAt: string;
  variantCount: number;
  blockers: GlobalBundleBlocker[];
};

const createColumns = (_isEluveAdmin: boolean) => {
  return new ColDefBuilder<OutputTemplateRow>()
    .linkSortable('name', (row) => `./${row.id}`)
    .defaultSortable('description', {
      cellRenderer: (row) =>
        row.description ? (
          <div className="max-w-md truncate" title={row.description}>
            {row.description}
          </div>
        ) : (
          <Text color="disabled">—</Text>
        ),
    })
    .defaultSortable('outputType', 'Output Type')
    .defaultBoolean('isActive', 'Active')
    .dateSortable('createdAt', 'Created At')
    .defaultSortable('variantCount', '# Variant')
    .colDef({
      header: 'Actions',
      cell: ({ row }) => (
        <Stack direction="horizontal" fullWidth={false} gap={1}>
          <SimpleTooltip
            label={`Create a new Output Template using '${row.original.name}' as a starting point`}
          >
            <Link to={`./clone/${row.original.id}`}>
              <Button
                variant="outline"
                aria-label={`Clone ${row.original.name}`}
              >
                <Icon name="CopyPlus" size="xl" />
              </Button>
            </Link>
          </SimpleTooltip>
          <ToggleOutputTemplateActiveAction
            outputTemplateId={row.original.id}
            blockers={row.original.blockers}
          />
        </Stack>
      ),
    })
    .build();
};

export const OutputTemplatesListPage: React.FC<
  OutputTemplatesListPageProps
> = () => {
  const isEluveAdmin = useIsEluveAdmin();
  const {
    data: { llmOutputTemplates },
  } = useSuspenseQuery(getOutputTemplatesQuery);

  const {
    data: { llmModelPromptTemplates: blockerRows },
  } = useSuspenseQuery(getActiveBundleBlockersQuery);

  const blockersByOutputTemplateId = useMemo(
    () => groupGlobalBlockersByFk(blockerRows, (row) => row.outputTemplateId),
    [blockerRows],
  );

  const columns = useMemo(() => createColumns(isEluveAdmin), [isEluveAdmin]);

  const rows = useMemo(() => {
    return llmOutputTemplates.map<OutputTemplateRow>(
      ({
        id,
        isActive,
        name,
        description,
        llmOutputType,
        createdAt,
        variantsAggregate,
      }) => ({
        id,
        name,
        description: description ?? '',
        outputType: llmOutputType,
        isActive,
        createdAt,
        variantCount: variantsAggregate?.aggregate?.count ?? 0,
        blockers: blockersByOutputTemplateId.get(id) ?? [],
      }),
    );
  }, [llmOutputTemplates, blockersByOutputTemplateId]);

  return (
    <>
      <Stack justify="between" gap={2} direction="horizontal">
        <div className="flex-1">
          <div className="my-2 py-2">
            <Heading>Output Templates</Heading>
          </div>
        </div>
        <LinkButton to="create">
          <Icon name="Plus" size="auto" />
          Create an Output Template
        </LinkButton>
      </Stack>
      <div>
        <DataTable
          columns={columns}
          data={rows}
          enableGlobalSearch
          initialSortingState={[{ id: 'createdAt', desc: true }]}
        />
      </div>
    </>
  );
};
